import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Bell, X, Briefcase, Award, MapPin, Users, Calendar, Eye } from 'lucide-react';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { formatTimestamp } from '../../utils/timestampUtils';

const Vacancies = () => {
  const userProfile = null; // Mock Profile since AuthContext is removed
  const [activeTab, setActiveTab] = useState('vacancies');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedOrganization, setSelectedOrganization] = useState('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [showVacancyDetails, setShowVacancyDetails] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState('');

  // Real data from Firebase
  const [vacancies, setVacancies] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [examNotifications, setExamNotifications] = useState([]);

  // Load data from Firebase
  useEffect(() => {
    loadVacancyData();
  }, []);

  const loadVacancyData = async () => {
    try {
      setLoading(true);
      // Load vacancies
      const vacanciesRef = collection(db, 'vacancies');
      const vacanciesSnapshot = await getDocs(vacanciesRef);
      const vacanciesData = vacanciesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load exam results
      const resultsRef = collection(db, 'examResults');
      const resultsSnapshot = await getDocs(resultsRef);
      const resultsData = resultsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load exam notifications
      const notificationsRef = collection(db, 'examNotifications');
      const notificationsSnapshot = await getDocs(notificationsRef);
      const notificationsData = notificationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setVacancies(vacanciesData);
      setExamResults(resultsData);
      setExamNotifications(notificationsData);

      } catch (error) {
      setError('Failed to load vacancy data. Please try again later.');
      // Set empty arrays instead of mock data
      setVacancies([]);
      setExamResults([]);
      setExamNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredVacancies = vacancies.filter(vacancy => {
    const matchesSearch = vacancy.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || vacancy.category === selectedCategory;
    const matchesOrganization = selectedOrganization === 'all' || vacancy.organization === selectedOrganization;
    return matchesSearch && matchesCategory && matchesOrganization;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      // Use the formatTimestamp utility which handles Firestore timestamps properly
      return formatTimestamp(dateString, { includeTime: false, short: true });
    } catch (error) {
      // Error formatting date
      return 'Invalid Date';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-3 sm:mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Loading vacancies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pb-4 sm:pb-8 pt-2">
        {/* Header */}
        <div className="mb-4 sm:mb-8">
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
            Job Vacancies & Exam Updates
          </h1>
          <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">
            Stay updated with latest government job notifications and exam results
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-4 sm:mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto scrollbar-hide -mx-3 sm:mx-0 px-3 sm:px-0">
              <button
                onClick={() => setActiveTab('vacancies')}
                className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'vacancies'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                Job Vacancies
              </button>
              <button
                onClick={() => setActiveTab('results')}
                className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'results'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                Exam Results
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${activeTab === 'notifications'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                Notifications
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        {activeTab === 'vacancies' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredVacancies.map((vacancy, index) => (
              <motion.div
                key={vacancy.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {vacancy.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                    {vacancy.organization}
                  </p>
                  
                  <div className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4">
                    <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">{vacancy.location}</span>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span>{vacancy.totalPosts} posts</span>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 flex-shrink-0" />
                      <span>Last Date: {formatDate(vacancy.lastDate)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                    <button
                      onClick={() => {
                        setSelectedVacancy(vacancy);
                        setShowVacancyDetails(true);
                      }}
                      className="flex-1 px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center"
                    >
                      <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                      View Details
                    </button>
                    {vacancy.applicationLink && (
                       <a
                         href={vacancy.applicationLink}
                         target="_blank"
                         rel="noopener noreferrer"
                         className="px-3 py-2 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center flex-shrink-0"
                       >
                         <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                       </a>
                     )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {activeTab === 'results' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {examResults.map((result, index) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6"
              >
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate flex-1 mr-2">
                    {result.examName}
                  </h3>
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0 ${
                    result.status === 'published' ? 'bg-green-100 text-green-800' :
                    result.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {result.status}
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">
                  {result.organization}
                </p>
                {result.examType && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">
                    Type: {result.examType}
                  </p>
                )}
                {result.totalCandidates && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">
                    Candidates: {result.totalCandidates}
                  </p>
                )}
                {result.examDate && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">
                    Exam Date: {formatDate(result.examDate)}
                  </p>
                )}
                {result.resultDate && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 sm:mb-3">
                    Result Date: {formatDate(result.resultDate)}
                  </p>
                )}
                <div className="flex space-x-2">
                  {result.resultLink && (
                    <a
                      href={result.resultLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700 text-center"
                    >
                      View Result
                    </a>
                  )}

                </div>
              </motion.div>
            ))}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-3 sm:space-y-4">
            {examNotifications.map((notification, index) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-2 sm:mb-3">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex-1 min-w-0">
                    {notification.title}
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap ${
                      notification.type === 'schedule' ? 'bg-blue-100 text-blue-800' :
                      notification.type === 'result' ? 'bg-green-100 text-green-800' :
                      notification.type === 'admit_card' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {notification.type}
                    </span>
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full whitespace-nowrap ${
                      notification.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                      notification.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      notification.priority === 'normal' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {notification.priority}
                    </span>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2">
                  {notification.message}
                </p>
                {notification.organization && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">
                    Organization: {notification.organization}
                  </p>
                )}
                {notification.examName && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1.5 sm:mb-2">
                    Exam: {notification.examName}
                  </p>
                )}
                {notification.notificationDate && (
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 sm:mb-3">
                    Date: {formatDate(notification.notificationDate)}
                  </p>
                )}
                <div className="flex space-x-2">
                  {notification.link && (
                    <a
                      href={notification.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                      View Details
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
       </div>

       {/* Vacancy Details Modal */}
       {showVacancyDetails && selectedVacancy && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
           <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg max-w-2xl w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
             <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between">
                 <h2 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate pr-2">
                   {selectedVacancy.title}
                 </h2>
                 <button
                   onClick={() => setShowVacancyDetails(false)}
                   className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0 p-1"
                 >
                   <X className="w-5 h-5 sm:w-6 sm:h-6" />
                 </button>
               </div>
             </div>
             
             <div className="p-4 sm:p-6">
               <div className="space-y-3 sm:space-y-4">
                 <div>
                   <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                     Organization
                   </h3>
                   <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{selectedVacancy.organization}</p>
                 </div>
                 
                 <div>
                   <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                     Location
                   </h3>
                   <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{selectedVacancy.location}</p>
                 </div>
                 
                 <div>
                   <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                     Total Posts
                   </h3>
                   <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{selectedVacancy.totalPosts}</p>
                 </div>
                 
                 <div>
                   <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                     Last Date to Apply
                   </h3>
                   <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{formatDate(selectedVacancy.lastDate)}</p>
                 </div>
                 
                 {selectedVacancy.description && (
                   <div>
                     <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                       Description
                     </h3>
                     <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400">{selectedVacancy.description}</p>
                   </div>
                 )}
                 
                 <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 pt-3 sm:pt-4">
                    {selectedVacancy.applicationLink ? (
                      <a
                        href={selectedVacancy.applicationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 text-center"
                      >
                        Apply Now
                      </a>
                    ) : (
                      <button
                        disabled
                        className="flex-1 px-3 sm:px-4 py-2 bg-gray-400 text-white text-sm rounded-lg cursor-not-allowed"
                        title="Application link not available"
                      >
                        Apply Now (Link Not Available)
                      </button>
                    )}
                    <button
                      onClick={() => setShowVacancyDetails(false)}
                      className="px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Close
                    </button>
                  </div>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default Vacancies;
